import useInfinityScroll from '@/common/hooks/useInfinityScroll';
import { getVehicleFuelQueryOptions } from '@/common/queries/selections/getVehicleFuel/getVehicleFuelQueryOptions';
import { InsuranceABV } from '@/common/types/SelectionOptions.type';
import { ADJUSTER_REPORT_CULPRIT_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/culprit/culpritLinks';
import { ADJUSTER_REPORT_DAMAGED_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/damaged/damagedLinks';
import useVehicleInformationSelections from '@/modules/adjusterReportModules/IncidentParticipants/data/VehicleInformationSelections/useVehicleInformationSelections';
import { INITIAL_VISIT_LINKS } from '@/modules/adjusterReportModules/IncidentParticipants/pages/damaged/initialVisit/initialVisitLink';
import { getCulpritVehicleInformationOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getCulpritVehicleInformation/getCulpritVehicleInformationOption';
import { GetVehicleInformationType } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getVehicleInformation/getVehicleInformation.type';
import { getVehicleInformationOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getVehicleInformation/getVehicleInformationOption';
import { getVehicleInitialInformationOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getVehicleInitialInformation/getVehicleInitialInformationOption';
import { PatchCulpritPartOneVehicleInformationOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchCulpritPartOneVehicleInformation/patchCulpritPartOneVehicleInformationOption';
import { PatchPartOneVehicleInformationOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchPartOneVehicleInformation/patchPartOneVehicleInformationOption';
import {
  VehicleInformationEnum,
  VehicleInformationPartOneFormType,
} from '@/modules/adjusterReportModules/IncidentParticipants/types/VehicleInformationType';
import { fileDetailQueryOption } from '@/modules/FileList/queries/fileDetail/fileDetailQueryOption';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

const usePartOneHook = (type: VehicleInformationEnum) => {
  const methods = useForm<VehicleInformationPartOneFormType>();
  const { id, damagedId } = useParams();
  const [name, setName] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const VehicleTypeWatch = useWatch({
    control: methods.control,
    name: 'vehicle_type',
  });
  const VehiclePlateDesignWatch = useWatch({
    control: methods.control,
    name: 'vehicle_plate_design',
  });
  const VehiclePlateTypeWatch = useWatch({
    control: methods.control,
    name: 'vehicle_plate_type',
  });
  const { data: fileDetail } = useQuery(fileDetailQueryOption({ id: +id! }));
  const navigate = useNavigate();

  const { data: vehicleFuel } = useQuery(getVehicleFuelQueryOptions());

  const insurance = fileDetail?.insurance as InsuranceABV;
  const vehicleTypeValue = VehicleTypeWatch?.value;
  const vehicleTypeString = vehicleTypeValue ? vehicleTypeValue.toString() : '';

  const vehicleSystemsDependenciesMemo = useMemo(
    () => ({ insurance, type: vehicleTypeString, name: name ?? undefined }),
    [VehicleTypeWatch, name],
  );
  const vehicleTypeWatchValue = VehicleTypeWatch?.value;

  const vehicleUsesDependenciesMemo = useMemo(
    () => ({
      insurance: fileDetail?.insurance as InsuranceABV,
      type: vehicleTypeWatchValue?.toString() ?? '',
    }),
    [VehicleTypeWatch?.value?.toString(), fileDetail],
  );
  const plateTypesDependenciesMemo = useMemo(
    () => ({
      type: VehicleTypeWatch?.value?.toString() ?? '',
      insurance: fileDetail?.insurance as InsuranceABV,
    }),
    [VehicleTypeWatch?.value?.toString(), fileDetail],
  );
  const plateDesignDependenciesMemo = useMemo(
    () => ({
      type: VehicleTypeWatch?.value?.toString() ?? '',
      insurance: fileDetail?.insurance as InsuranceABV,
    }),
    [VehicleTypeWatch?.value?.toString(), fileDetail],
  );
  const plateSamplesDependenciesMemo = useMemo(
    () => ({
      type: VehicleTypeWatch?.value?.toString() ?? ' ',
      insurance: fileDetail?.insurance as InsuranceABV,
      plate_type: VehiclePlateTypeWatch?.value?.toString() ?? '',
      plate_design: VehiclePlateDesignWatch?.value.toString() ?? '',
    }),
    [
      VehicleTypeWatch?.value?.toString(),
      VehiclePlateTypeWatch?.value?.toString(),
      VehiclePlateDesignWatch?.value?.toString(),
    ],
  );
  const data = useVehicleInformationSelections({
    vehicleSystemsDependencies: vehicleSystemsDependenciesMemo,
    vehicleUsesDependencies: vehicleUsesDependenciesMemo,
    plateTypesDependencies: plateTypesDependenciesMemo,
    plateDesignDependencies: plateDesignDependenciesMemo,
    plateSamplesDependencies: plateSamplesDependenciesMemo,
  });
  const { mutate: mutatePatchPartOneVehicleInformation } = useMutation(
    PatchPartOneVehicleInformationOption(),
  );
  const { mutate: mutatePatchCulpritPartOneVehicleInformation, isPending } =
    useMutation(PatchCulpritPartOneVehicleInformationOption(type));

  const { observerRefCallback } = useInfinityScroll(
    data.fetchVehicleSystemsNextPage,
  );

  const queryOptions = useMemo(() => {
    if (type === VehicleInformationEnum.damaged) {
      return getVehicleInformationOption({
        damaged_id: +damagedId!,
        id: +id!,
        type: 'property',
      });
    } else if (type === VehicleInformationEnum.culprit) {
      return getCulpritVehicleInformationOption({ id: +id! });
    } else if (type === VehicleInformationEnum.initial) {
      return getVehicleInitialInformationOption({ id: +id! });
    }
    return { queryKey: ['dummy'] as const, enabled: false } as const;
  }, [type, id, damagedId]);

  const { data: vehicleInformationData, isPending: vehicleInformationLoading } =
    useQuery<GetVehicleInformationType>(queryOptions as any);

  const submitHandler =
    type === VehicleInformationEnum.damaged
      ? (data: VehicleInformationPartOneFormType) => {
          mutatePatchPartOneVehicleInformation(
            {
              damaged_id: +damagedId!,
              id: +id!,
              data: {
                vehicle_type: data.vehicle_type?.value,
                vehicle_model: data.vehicle_model?.value,
                vehicle_use: data.vehicle_use?.value,
                vehicle_plate_type: data.vehicle_plate_type?.value,
                vehicle_plate_design: data.vehicle_plate_design?.value,
                vehicle_plate: data.vehicle_plate?.plateString,
                vehicle_plate_sample: data?.vehicle_plate_sample?.value!,
              },
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey: queryOptions.queryKey,
                });
                navigate(
                  ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
                    damagedId: damagedId!,
                    id: id!,
                  }).VEHICLE_INFORMATION.partTwo,
                  { replace: true },
                );
              },
            },
          );
        }
      : (data: VehicleInformationPartOneFormType) => {
          mutatePatchCulpritPartOneVehicleInformation(
            {
              id: +id!,
              data: {
                vehicle_type: data.vehicle_type?.value,
                vehicle_model: data.vehicle_model?.value,
                vehicle_use: data.vehicle_use?.value,
                vehicle_plate_type: data.vehicle_plate_type?.value,
                vehicle_plate_design: data.vehicle_plate_design?.value,
                vehicle_plate: data.vehicle_plate?.plateString,
                vehicle_fuel: data.vehicle_fuel?.value,
                vehicle_plate_sample: data?.vehicle_plate_sample?.value!,
                vehicle_insurance_extended: data?.vehicle_insurance_extended,
              },
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey: queryOptions.queryKey,
                });
                if (type === VehicleInformationEnum.culprit) {
                  navigate(
                    ADJUSTER_REPORT_CULPRIT_LINKS.CULPRIT_INFORMATION({
                      id: +id!,
                    }).VEHICLE_INFORMATION.partTwo,
                    { replace: true },
                  );
                } else {
                  navigate(INITIAL_VISIT_LINKS.PART_TWO(Number(id)!), {
                    replace: true,
                  });
                }
              },
            },
          );
        };

  useEffect(() => {
    if (vehicleInformationData) {
      methods.reset({
        vehicle_plate: {
          plate: {
            vehicle_plate_bottom: vehicleInformationData?.vehicle_plate_bottom,
            vehicle_plate_top: vehicleInformationData?.vehicle_plate_top,
            iran: vehicleInformationData?.vehicle_plate_iran,
            left: vehicleInformationData?.vehicle_plate_left,
            right: vehicleInformationData?.vehicle_plate_right,
            letter: {
              label: vehicleInformationData?.vehicle_plate_letter?.letter,
              value:
                vehicleInformationData?.vehicle_plate_letter?.id?.toString(),
            },
          },
          plateString: vehicleInformationData?.vehicle_plate,
        },
        vehicle_model: vehicleInformationData?.vehicle_model
          ? {
              label: vehicleInformationData?.vehicle_model?.name,
              value: vehicleInformationData?.vehicle_model?.id,
            }
          : null,
        vehicle_plate_design: vehicleInformationData?.vehicle_plate_design
          ? {
              label: vehicleInformationData?.vehicle_plate_design?.fa_const,
              value: vehicleInformationData?.vehicle_plate_design?.const,
            }
          : null,

        vehicle_plate_type: vehicleInformationData?.vehicle_plate_type
          ? {
              label: vehicleInformationData?.vehicle_plate_type?.name,
              value: vehicleInformationData?.vehicle_plate_type?.id,
            }
          : null,
        vehicle_plate_sample: vehicleInformationData?.vehicle_plate_sample
          ? {
              label: vehicleInformationData?.vehicle_plate_sample?.title,
              value: vehicleInformationData?.vehicle_plate_sample?.id,
            }
          : null,
        vehicle_type: vehicleInformationData?.vehicle_type
          ? {
              label: vehicleInformationData?.vehicle_type?.fa_name,
              value: vehicleInformationData?.vehicle_type?.id,
            }
          : null,
        vehicle_use: vehicleInformationData?.vehicle_use
          ? {
              label: vehicleInformationData?.vehicle_use?.name,
              value: vehicleInformationData?.vehicle_use?.id,
            }
          : null,
        vehicle_fuel: vehicleInformationData?.vehicle_fuel
          ? {
              label: vehicleInformationData?.vehicle_fuel?.fa_const,
              value: vehicleInformationData?.vehicle_fuel?.const,
            }
          : null,
        vehicle_insurance_extended:
          vehicleInformationData.vehicle_insurance_extended,
      });
      methods.setValue('vehicle_plate', {
        plate: {
          vehicle_plate_bottom: vehicleInformationData?.vehicle_plate_bottom,
          vehicle_plate_top: vehicleInformationData?.vehicle_plate_top,
          iran: vehicleInformationData?.vehicle_plate_iran,
          left: vehicleInformationData?.vehicle_plate_left,
          right: vehicleInformationData?.vehicle_plate_right,
          letter: {
            label: vehicleInformationData?.vehicle_plate_letter?.letter,
            value: vehicleInformationData?.vehicle_plate_letter?.id?.toString(),
          },
        },
        plateString: vehicleInformationData?.vehicle_plate ?? '',
      });
    }
  }, [vehicleInformationData, type, methods, vehicleInformationLoading]);
  useEffect(() => {
    if (methods?.formState?.isDirty) {
      methods.setValue('vehicle_plate_sample', null);
    }
  }, [
    VehiclePlateTypeWatch?.value?.toString(),
    VehiclePlateDesignWatch?.value?.toString(),
  ]);
  useEffect(() => {
    if (methods?.formState?.isDirty) {
      methods.setValue('vehicle_use', null);
      methods.setValue('vehicle_model', null);
      methods.setValue('vehicle_plate_type', null);
      methods.setValue('vehicle_plate_design', null);
      methods.setValue('vehicle_plate_sample', null);
      methods.setValue('vehicle_plate.plate.vehicle_plate_bottom', '');
      methods.setValue('vehicle_plate.plate.iran', '');
      methods.setValue('vehicle_plate.plate.left', '');
      methods.setValue('vehicle_plate.plate.letter', undefined);
      methods.setValue('vehicle_plate.plate.right', undefined);
      methods.setValue('vehicle_plate.plate.vehicle_plate_top', undefined);
      methods.setValue('vehicle_plate', {
        plate: {
          vehicle_plate_bottom: '',
          iran: '',
          left: '',
          letter: {
            label: '',
            value: '',
          },
          right: '',
          vehicle_plate_top: '',
        },
        plateString: '',
      });
    }
  }, [VehicleTypeWatch?.value]);
  useEffect(() => {
    if (Object.keys(methods?.formState?.touchedFields).length) {
      methods.setValue('vehicle_plate.plate.vehicle_plate_bottom', '');
      methods.setValue('vehicle_plate.plate.iran', '');
      methods.setValue('vehicle_plate.plate.left', '');
      methods.setValue('vehicle_plate.plate.letter', undefined);
      methods.setValue('vehicle_plate.plate.right', undefined);
      methods.setValue('vehicle_plate.plate.vehicle_plate_top', undefined);
      methods.setValue('vehicle_plate', {
        plate: {
          vehicle_plate_bottom: '',
          iran: '',
          left: '',
          letter: {
            label: '',
            value: '',
          },
          right: '',
          vehicle_plate_top: '',
        },
        plateString: '',
      });
    }
  }, [VehiclePlateDesignWatch?.value?.toString()]);

  return {
    methods,
    data,
    observerRefCallback,
    vehicleInformationData,
    submitHandler,
    VehicleTypeWatch,
    VehiclePlateDesignWatch,
    VehiclePlateTypeWatch,
    setName,
    vehicleFuel,
    isPending,
    vehicleInformationLoading,
  };
};
export default usePartOneHook;
